import React from 'react'
import moment from 'moment'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { Button, Modal, Text } from '@mantine/core'

import {
  CreateEventSchema,
  CreateEventType,
  UpdateEventType,
  EventBaseType,
} from '@customTypes/event'

import QueryKeys from '@constants/queryKeys'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import { useIsMobile } from '@hooks/mobile'

import { ErrorService } from '@utils/error'
import { isLoadingMutation } from '@utils/network'

import Input from '@components/shared/input'
import { Dropzone } from '@components/shared/dropzone'
import ConfirmationModal from '@components/shared/confirmationModal'
import { ModalProps } from '@customTypes/page'
import { buildSelectOptions, filterSelect } from '@utils/form'
import { IconCheck, IconCircleFilled } from '@tabler/icons-react'
import { $ } from '@utils/styles'

export type EventFormType = {
  clientID?: string | null
  initialStatus?: string
  initialType?: string
  event?: EventBaseType
}

const INITIAL: CreateEventType['data'] = {
  name: '',
  date: moment().unix(),
  endDate: moment().add(1, 'hour').unix(),
  status: {} as EventBaseType['status'],
  type: {} as EventBaseType['type'],
  description: '',
  contacts: [],
  amount: undefined,
  attachments: [],
  client: {} as EventBaseType['client'],
}

const EventForm = (props: ModalProps<EventFormType>) => {
  const { open, modal, data = {} as EventFormType, size = 'lg', onClose: outerOnClose } = props

  const [confirmExit, setConfirmExit] = React.useState(false)

  const [confirmDelete, setConfirmDelete] = React.useState(false)

  const isMobile = useIsMobile()

  const formEvent = useForm<CreateEventType['data']>({
    initialValues: INITIAL,
    validate: zodResolver(CreateEventSchema.shape.data),
  })

  const clientsQuery = useQuery({
    queryKey: [QueryKeys.GET_CLIENTS_KEY],
    queryFn: async () => {
      const response = await DataRepo.clientEntityService.getClients({})
      return response
    },
  })

  const { client } = formEvent.getValues()

  const contactsQuery = useQuery({
    enabled: Boolean(client.uid),
    queryKey: [QueryKeys.GET_CLIENT_KEY, client.uid],
    queryFn: async ({ queryKey }) => {
      const [, clientId] = queryKey
      const response = await DataRepo.clientEntityService.getClientById(clientId)
      return response
    },
    select: ({ contacts = [] }) => contacts,
  })

  const eventStatusQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventStatusOptions(),
  })

  const eventTypesQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventTypeOptions(),
  })

  const eventCreateMutation = useMutation<EventBaseType, ErrorService, CreateEventType>({
    mutationFn: async (dataCreate) => {
      const response = await DataRepo.clientEntityService.createEvent(dataCreate)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_EVENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al crear el evento',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Evento creado correctamente',
      })

      onClose?.()
    },
  })

  const eventUpdateMutation = useMutation<void, ErrorService, UpdateEventType>({
    mutationFn: async (dataUpdate) => {
      const response = await DataRepo.clientEntityService.updateEvent(dataUpdate)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_EVENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_KEY, dataUpdate.data.uid],
        refetchType: 'all',
        exact: true,
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al actualizar el evento',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Evento actualizado correctamente',
      })

      onClose?.()
    },
  })

  const eventDeleteMutation = useMutation<void, ErrorService, string>({
    mutationFn: async (uid) => {
      const response = await DataRepo.clientEntityService.deleteEvent(uid)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_EVENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al eliminar el evento',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Evento eliminado correctamente',
      })

      onClose?.()
    },
  })

  React.useEffect(() => {
    if (open && data?.event) {
      formEvent.setValues(data.event)
    } else if (!open) {
      formEvent.setValues(INITIAL)
    }

    if (data.initialStatus) {
      const statusData = eventStatusQuery.data?.find((s) => s.uid === data.initialStatus)
      if (statusData) {
        formEvent.setFieldValue('status', statusData, {
          forceUpdate: true,
        })
      }
    }
    if (data.initialType) {
      const typeData = eventTypesQuery.data?.find((s) => s.uid === data.initialType)
      if (typeData) {
        formEvent.setFieldValue('type', typeData, {
          forceUpdate: true,
        })
      }
    }
    formEvent.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.event, data?.initialStatus, data?.initialType, open])

  React.useEffect(() => {
    if (clientsQuery.data && data?.clientID) {
      const clientData = clientsQuery.data.find((s) => s.uid === data?.clientID)
      if (clientData) {
        formEvent.setFieldValue(
          'client',
          {
            name: clientData.name,
            ruc: clientData.ruc,
            uid: clientData.uid,
          },
          {
            forceUpdate: true,
          },
        )
        formEvent.resetDirty()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientsQuery.data, data?.clientID])

  const isLoading = isLoadingMutation(eventCreateMutation, eventUpdateMutation)

  const { event } = data

  const content = (
    <form
      className="cd-flex cd-flex-col cd-gap-y-[1rem]"
      onSubmit={formEvent.onSubmit((values) => {
        if (!formEvent.values.client.uid) {
          notifications.show({
            color: 'red',
            title: 'Error',
            message: 'Debe seleccionar un cliente',
          })
          return
        }

        if (data.event && event) {
          eventUpdateMutation.mutate({
            clientID: formEvent.values.client.uid,
            data: {
              ...values,
              uid: event.uid,
            },
          })
        } else {
          eventCreateMutation.mutate({
            clientID: formEvent.values.client.uid,
            data: values,
          })
        }
      })}
    >
      <Input
        data={buildSelectOptions({
          data: clientsQuery.data,
          value: 'uid',
          label: ({ name, ruc }) => `${name}${ruc ? ` - ${ruc}` : ''}`,
        })}
        disabled={Boolean(data.clientID)}
        filter={(input) =>
          filterSelect({
            search: input.search,
            options: input.options,
          })
        }
        label="Cliente"
        loading={isLoadingMutation(clientsQuery)}
        placeholder="Seleccione un cliente"
        readOnly={Boolean(data.clientID)}
        typeInput="select"
        {...formEvent.getInputProps('client.name')}
        value={formEvent.values.client.uid || ''}
        onBlur={() => formEvent.validateField('client.name')}
        onChange={(value) => {
          const clientData = clientsQuery.data?.find((s) => s.uid === value)

          if (!clientData) {
            notifications.show({
              color: 'red',
              title: 'Error',
              message: 'Debe seleccionar un cliente válido',
            })
            return
          }

          formEvent.setFieldValue(
            'client',
            {
              name: clientData.name,
              ruc: clientData.ruc,
              uid: clientData.uid,
            },
            {
              forceUpdate: true,
            },
          )
        }}
      />

      <div className="cd-flex cd-flex-col cd-gap-y-[1rem] md:cd-flex-row md:cd-gap-x-[1rem]">
        <Input
          className="cd-basis-full md:cd-basis-1/2"
          data={buildSelectOptions({
            data: eventStatusQuery.data,
            value: 'uid',
            label: 'name',
          })}
          disabled={Boolean(data.initialStatus)}
          filter={(input) =>
            filterSelect({
              search: input.search,
              options: input.options,
            })
          }
          label="Estado"
          loading={isLoadingMutation(eventStatusQuery)}
          placeholder="Seleccione un estado"
          renderOption={(option) => (
            <div className={$('cd-w-full cd-flex cd-items-center cd-gap-x-2 cd-justify-between')}>
              <div className="cd-flex cd-items-center cd-gap-x-2">
                <IconCircleFilled
                  color={
                    eventStatusQuery.data?.find((s) => s.uid === option.option.value)?.color ||
                    '#000'
                  }
                  size={16}
                />
                <Text
                  className={$(
                    'cd-text-gray-800 dark:cd-text-gray-200',
                    option.checked && '!cd-font-bold',
                  )}
                >
                  {option.option.label}
                </Text>
              </div>
              {option.checked && <IconCheck />}
            </div>
          )}
          typeInput="select"
          {...formEvent.getInputProps('status.name')}
          value={formEvent.values.status?.uid || ''}
          onBlur={() => formEvent.validateField('status.name')}
          onChange={(value) => {
            const statusData = eventStatusQuery.data?.find((s) => s.uid === value)

            if (!statusData) {
              return
            }

            formEvent.setFieldValue('status', statusData, {
              forceUpdate: true,
            })
          }}
        />

        <Input
          className="cd-basis-full md:cd-basis-1/2"
          data={buildSelectOptions({
            data: eventTypesQuery.data,
            value: 'uid',
            label: 'name',
          })}
          disabled={Boolean(data.initialType)}
          filter={(input) =>
            filterSelect({
              search: input.search,
              options: input.options,
            })
          }
          label="Tipo"
          loading={isLoadingMutation(eventTypesQuery)}
          placeholder="Seleccione un tipo"
          renderOption={(option) => (
            <div className={$('cd-w-full cd-flex cd-items-center cd-gap-x-2 cd-justify-between')}>
              <div className="cd-flex cd-items-center cd-gap-x-2">
                <IconCircleFilled
                  color={
                    eventTypesQuery.data?.find((s) => s.uid === option.option.value)?.color ||
                    '#000'
                  }
                  size={16}
                />
                <Text
                  className={$(
                    'cd-text-gray-800 dark:cd-text-gray-200',
                    option.checked && '!cd-font-bold',
                  )}
                >
                  {option.option.label}
                </Text>
              </div>
              {option.checked && <IconCheck />}
            </div>
          )}
          typeInput="select"
          {...formEvent.getInputProps('type.name')}
          value={formEvent.values.type?.uid || ''}
          onBlur={() => formEvent.validateField('type.name')}
          onChange={(value) => {
            const typeData = eventTypesQuery.data?.find((s) => s.uid === value)

            if (!typeData) {
              notifications.show({
                color: 'red',
                title: 'Error',
                message: 'Debe seleccionar un tipo válido',
              })
              return
            }

            formEvent.setFieldValue('type', typeData, {
              forceUpdate: true,
            })
          }}
        />
      </div>

      <Input
        label="Nombre"
        placeholder="Escriba el nombre del evento"
        typeInput="text"
        {...formEvent.getInputProps('name')}
      />

      <Input
        label="Descripción"
        placeholder="Describa el evento"
        typeInput="textarea"
        {...formEvent.getInputProps('description')}
      />

      <div className="cd-flex cd-flex-col cd-gap-y-[1rem] md:cd-flex-row md:cd-gap-x-[1rem]">
        <Input
          className="cd-basis-full md:cd-basis-1/2"
          label="Fecha inicio"
          minDate={moment().startOf('day').toDate()}
          placeholder="Seleccione la fecha del evento"
          typeInput="dateTimePicker"
          {...formEvent.getInputProps('date')}
          value={moment.unix(formEvent.values.date).toDate()}
          onChange={(date) => {
            if (date) {
              formEvent.setFieldValue('date', moment(date).unix())
            }
          }}
        />

        <Input
          className="cd-basis-full md:cd-basis-1/2"
          label="Duración (minutos)"
          min={1}
          placeholder="Ingrese la duración del evento"
          typeInput="number"
          {...formEvent.getInputProps('endDate')}
          value={moment
            .unix(formEvent.values.endDate)
            .diff(moment.unix(formEvent.values.date), 'minutes')}
          onChange={(value) => {
            const endDate = moment.unix(formEvent.values.date).clone().add(value, 'minutes')

            formEvent.setFieldValue('endDate', endDate.unix())
          }}
        />
      </div>
      <Input
        className="cd-basis-full md:cd-basis-1/2"
        label="Gasto (opcional)"
        min={0}
        placeholder="Ingrese el gasto del evento"
        prefix="$"
        typeInput="number"
        {...formEvent.getInputProps('amount')}
      />

      <Input
        clearable
        hidePickedOptions
        className="cd-mb-4"
        data={buildSelectOptions({
          data: contactsQuery.data,
          value: 'uid',
          label: ({ name, email, phone }) => {
            let contact = name
            if (email) {
              contact += ` - ${email}`
            }
            if (phone) {
              contact += ` - ${phone}`
            }
            return contact
          },
        })}
        filter={(input) =>
          filterSelect({
            search: input.search,
            options: input.options,
          })
        }
        label="Contactos"
        loading={isLoadingMutation(contactsQuery)}
        maxValues={4}
        placeholder="Seleccione contactos"
        typeInput="multiSelect"
        {...formEvent.getInputProps('contacts')}
        value={formEvent.values.contacts?.map((c) => c.uid)}
        onBlur={() => formEvent.validateField('contacts')}
        onChange={(value) => {
          const contactsData = contactsQuery.data?.filter((s) => value.includes(s.uid)) || []

          formEvent.setFieldValue('contacts', contactsData, {
            forceUpdate: true,
          })
        }}
      />

      <Dropzone
        multiple
        disabled={isLoading || formEvent.values.attachments.length === 4}
        files={formEvent.values.attachments ? formEvent.values.attachments : []}
        label="Imágenes"
        maxFiles={4}
        onDrop={(files) => {
          formEvent.setFieldValue('attachments', files)
        }}
      />

      <Button
        color="red"
        loaderProps={{ type: 'dots' }}
        loading={isLoadingMutation(eventDeleteMutation)}
        variant="subtle"
        onClick={() => {
          if (!event) return
          setConfirmDelete(true)
        }}
      >
        Eliminar evento
      </Button>

      <Button loaderProps={{ type: 'dots' }} loading={isLoading} type="submit">
        {`${event ? 'Actualizar' : 'Crear'} evento`}
      </Button>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={event ? 'Editar evento' : 'Nuevo evento'}
        onClose={() => {
          if (formEvent.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>

        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Eliminar"
          open={confirmDelete}
          title="Eliminar evento"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            if (!event) return
            eventDeleteMutation.mutate(event.uid)
            setConfirmDelete(false)
          }}
        >
          <Text className="cd-text-base">
            ¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer.
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return content

  function onClose() {
    setConfirmExit(false)
    outerOnClose?.()
  }
}

export default EventForm
